import React, { useState, useEffect, useRef, useCallback } from 'react';
import ImageWrapDropdown from './ImageWrapDropdown';
import { Trash2 } from 'lucide-react';

/**
 * ImageFloatingBar
 * Google Docs style floating action pill positioned beneath the selected image.
 * Floatable & Draggable anywhere across the editor.
 * Matches the user's reference screenshot:
 * ( [Wrap icon ▾] | Image options | [Red Trash Can] )
 */
const ImageFloatingBar = ({
  selectedImg,
  wrapperRef,
  imageToolbarPos,
  currentWrapMode,
  onSelectWrapMode,
  onToggleSidebar,
  isSidebarOpen,
  onDeleteImage,
}) => {
  const [autoPos, setAutoPos] = useState(imageToolbarPos || null);
  const [dragPos, setDragPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const barRef = useRef(null);

  // Automatically calculate position relative to selected image and wrapper
  const updatePosition = useCallback(() => {
    if (!selectedImg || !wrapperRef?.current) {
      setAutoPos(null);
      return;
    }

    const imgRect = selectedImg.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const contentEl = selectedImg.closest('.rich-text-content');
    const contentRect = contentEl ? contentEl.getBoundingClientRect() : wrapperRect;

    // If the image is completely scrolled out of view in the editor content, hide the bar
    if (imgRect.bottom < contentRect.top + 10 || imgRect.top > contentRect.bottom - 10) {
      setAutoPos(null);
      return;
    }

    const barEl = barRef.current;
    const barWidth = barEl ? barEl.offsetWidth : 230;
    const barHeight = barEl ? barEl.offsetHeight : 38;

    const imgTop = imgRect.top - wrapperRect.top;
    const imgBottom = imgRect.bottom - wrapperRect.top;
    const imgCenter = imgRect.left - wrapperRect.left + (imgRect.width / 2);

    // Keep pill comfortably inside wrapper with at least 16px clearance
    const halfWidth = barWidth / 2;
    const minCenter = halfWidth + 16;

    // If the sidebar is open on the right (375px wide), avoid under-lapping the sidebar
    const sidebarWidth = isSidebarOpen ? 375 : 0;
    const availableWidthOnScreen = window.innerWidth - sidebarWidth;
    const wrapperRightOnScreen = wrapperRect.left + wrapperRect.width;
    const effectiveWrapperWidth = (sidebarWidth > 0 && wrapperRightOnScreen > availableWidthOnScreen)
      ? Math.max(minCenter + 40, availableWidthOnScreen - wrapperRect.left)
      : wrapperRect.width;

    const maxCenter = Math.max(minCenter, effectiveWrapperWidth - halfWidth - 16);
    const clampedLeft = Math.max(minCenter, Math.min(maxCenter, imgCenter));

    // Determine whether to place below or above image
    const contentBottomOffset = contentRect.bottom - wrapperRect.top;
    const spaceBelow = contentBottomOffset - imgBottom;
    const placeAbove = spaceBelow < (barHeight + 14) && (imgTop - 8) > barHeight;

    let barTop = placeAbove
      ? Math.max(8, imgTop - barHeight - 8)
      : imgBottom + 8;

    // Safety clamp: keep bar comfortably within content area
    const maxBarTop = Math.max(8, contentBottomOffset - barHeight - 8);
    barTop = Math.min(maxBarTop, Math.max(8, barTop));

    setAutoPos({
      top: Math.round(barTop),
      left: Math.round(clampedLeft),
      placeAbove,
    });
  }, [selectedImg, wrapperRef, isSidebarOpen]);

  useEffect(() => {
    if (imageToolbarPos && !dragPos) {
      updatePosition();
    }
  }, [imageToolbarPos, dragPos, updatePosition]);

  useEffect(() => {
    updatePosition();
    // Reset manual drag position when a different image is selected
    setDragPos(null);

    const handleScrollOrResize = () => updatePosition();
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('scroll', handleScrollOrResize, true);

    const wrapper = wrapperRef?.current;
    if (wrapper) wrapper.addEventListener('scroll', handleScrollOrResize);

    const contentEl = selectedImg?.closest('.rich-text-content');
    if (contentEl) contentEl.addEventListener('scroll', handleScrollOrResize);

    // Real-time tracking via ResizeObserver on the selected image (handles resizing)
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && selectedImg) {
      resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });
      resizeObserver.observe(selectedImg);
    }

    // MutationObserver for style or class attribute changes on the image
    let mutationObserver = null;
    if (typeof MutationObserver !== 'undefined' && selectedImg) {
      mutationObserver = new MutationObserver(() => {
        updatePosition();
      });
      mutationObserver.observe(selectedImg, {
        attributes: true,
        attributeFilter: ['style', 'class', 'width', 'height'],
      });
    }

    return () => {
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      if (wrapper) wrapper.removeEventListener('scroll', handleScrollOrResize);
      if (contentEl) contentEl.removeEventListener('scroll', handleScrollOrResize);
      if (resizeObserver) resizeObserver.disconnect();
      if (mutationObserver) mutationObserver.disconnect();
    };
  }, [selectedImg, wrapperRef, updatePosition]);

  // Dragging handler to make the bar freely floatable/movable anywhere
  const handleMouseDown = (e) => {
    // Only drag on primary (left) mouse button and ignore clicks on interactive buttons/dropdown
    if (e.button !== 0) return;
    if (e.target.closest('button') || e.target.closest('.rte-gdocs-wrap-menu')) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startY = e.clientY;

    const currentLeft = dragPos ? dragPos.left : (autoPos ? autoPos.left : 0);
    const currentTop = dragPos ? dragPos.top : (autoPos ? autoPos.top : 0);

    setIsDragging(true);

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      const wrapperRect = wrapperRef?.current?.getBoundingClientRect();
      const barEl = barRef.current;
      const halfWidth = (barEl ? barEl.offsetWidth : 230) / 2;
      const barHeight = barEl ? barEl.offsetHeight : 38;

      let newLeft = currentLeft + deltaX;
      let newTop = currentTop + deltaY;

      // Keep within wrapper bounds during drag
      if (wrapperRect) {
        newLeft = Math.max(halfWidth + 8, Math.min(wrapperRect.width - halfWidth - 8, newLeft));
        newTop = Math.max(4, Math.min(wrapperRect.height - barHeight - 4, newTop));
      }

      setDragPos({
        left: Math.round(newLeft),
        top: Math.round(newTop),
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  // Double click resets floating position back to docking under the image
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setDragPos(null);
  };

  const finalTop = dragPos ? dragPos.top : (autoPos ? autoPos.top : null);
  const finalLeft = dragPos ? dragPos.left : (autoPos ? autoPos.left : null);

  if (!selectedImg || finalTop === null || finalLeft === null) return null;

  return (
    <div
      ref={barRef}
      className={`rte-gdocs-floating-bar ${isDragging ? 'is-dragging' : ''}`}
      style={{
        top: `${finalTop}px`,
        left: `${finalLeft}px`,
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onClick={(e) => e.stopPropagation()}
      title={dragPos ? 'Double-click to reset position | Drag to float anywhere' : 'Drag to float anywhere'}
    >
      {/* 1. Wrap Text Dropdown (Image 2 & 3) */}
      <ImageWrapDropdown
        currentWrapMode={currentWrapMode}
        onSelectWrapMode={onSelectWrapMode}
        buttonClassName="rte-gdocs-bar-btn"
      />

      {/* Divider */}
      <div className="rte-gdocs-bar-divider" />

      {/* 2. "Image options" blue text button (Image 4 & 5) */}
      <button
        type="button"
        className={`rte-gdocs-bar-btn-options-text ${isSidebarOpen ? 'active' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onToggleSidebar();
        }}
        title="Open Image options sidebar"
      >
        Image options
      </button>

      {/* Divider */}
      <div className="rte-gdocs-bar-divider" />

      {/* 3. Delete button (Red trash can) */}
      <button
        type="button"
        className="rte-gdocs-bar-btn-delete"
        onClick={(e) => {
          e.stopPropagation();
          onDeleteImage();
        }}
        title="Delete image"
      >
        <Trash2 className="w-4 h-4 text-red-600" />
      </button>
    </div>
  );
};

export default ImageFloatingBar;
